import type { Action } from '../../types/actions';
import type { ActionCardRenderer } from '../../types/plugins';
import { useMooConfig } from '../../provider';
import { useChatSend } from '../ChatPanel/chat-send-context';

export interface ActionCardSlotProps {
  readonly action: Action;
  readonly messageId: string;
  readonly correlationId: string | null;
  readonly isLastMessage: boolean;
  readonly originatingQuery?: string;
  readonly followupUserText?: string;
}

/** Renders a single action card by looking up a matching renderer from
 *  `MooChatConfig.actionCards`. Match priority is (type + subType) first,
 *  then (type only). Returns null when no renderer is registered — the
 *  library is intentionally silent about unknown card types so back-ends
 *  can roll forward without crashing older clients. */
export function ActionCardSlot({
  action,
  messageId,
  correlationId,
  isLastMessage,
  originatingQuery,
  followupUserText,
}: ActionCardSlotProps) {
  const { actionCards, transport } = useMooConfig();
  const send = useChatSend();
  const renderer = findRenderer(actionCards, action.type, action.subType);
  if (!renderer) return null;
  return (
    <>
      {renderer.render({
        type: action.type,
        subType: action.subType,
        data: action,
        messageId,
        correlationId,
        isLastMessage,
        originatingQuery,
        followupUserText,
        onSendMessage: send,
        executeAction: (payload) => transport.executeAction(payload),
      })}
    </>
  );
}

function findRenderer(
  list: ActionCardRenderer[] | undefined,
  type: string,
  subType: string | undefined,
): ActionCardRenderer | null {
  if (!list || list.length === 0) return null;
  if (subType) {
    const exact = list.find((r) => r.type === type && r.subType === subType);
    if (exact) return exact;
  }
  const typeOnly = list.find((r) => r.type === type && !r.subType);
  return typeOnly ?? null;
}
